// Estimate rules (technical design §6.6; Q8, Q44; audit F-06, F-17). Pure: the device predicts
// with the same code the server enforces.

/**
 * The most options one estimate (one problem) offers, and the most a saved option template holds
 * (Amir, 24 Oct: six choices for a problem, never more). The server, the office and the iPad all
 * read this one number so they can't drift apart.
 */
export const MAX_ESTIMATE_OPTIONS = 6;

/**
 * Up to three options sit side by side on the tech's and the office's screens; four or more become a
 * list, one option to a row, so no column gets too narrow to read. Tiers are optional labels, so
 * nothing here assumes three options or a tier on each.
 */
export const optionsSideBySide = (count: number): boolean => count <= 3;

export type OverrideVerdict =
  /** The book price, or a change the tech may make on their own (with a reason). */
  | { ok: true; approval: null }
  /** Allowed, but the office must OK it before the option can be sold. */
  | { ok: true; approval: 'needed'; why: 'below_limit' | 'below_cost' | 'below_floor' }
  | { ok: false; reason: string };

/**
 * Q44: a reason always; a tech may go up to `maxDiscountPct` below book on their own; deeper, or
 * below our cost, needs the office's OK; never below zero, but for a discount item at its own price.
 * Raising a price is the tech's call.
 * A line with no pricebook price (free-form, QA-015) always needs a reason, and below the
 * company's floor for such lines, or below a cost entered for it, the office's OK.
 */
export function checkOverride(line: {
  bookCents: number | null;
  priceCents: number;
  costCents: number | null;
  reason: string | null;
  maxDiscountPct: number;
  freeFormFloorCents: number;
}): OverrideVerdict {
  // a discount item's own price is below zero (tenant 0147): at that price it's the book price; any
  // other price below zero isn't
  const discountAtBook = line.bookCents !== null && line.bookCents < 0 && line.priceCents === line.bookCents;
  if (line.priceCents < 0 && !discountAtBook) return { ok: false, reason: 'A price cannot be below zero.' };
  if (line.bookCents === null) {
    if (!line.reason?.trim())
      return { ok: false, reason: 'Say why: a line that is not from the pricebook needs a reason.' };
    if (line.costCents !== null && line.priceCents < line.costCents)
      return { ok: true, approval: 'needed', why: 'below_cost' };
    if (line.priceCents < line.freeFormFloorCents)
      return { ok: true, approval: 'needed', why: 'below_floor' };
    return { ok: true, approval: null };
  }
  if (line.priceCents === line.bookCents) return { ok: true, approval: null };
  if (!line.reason?.trim()) return { ok: false, reason: 'Say why the price changed.' };
  if (line.costCents !== null && line.priceCents < line.costCents)
    return { ok: true, approval: 'needed', why: 'below_cost' };
  const floor = Math.ceil((line.bookCents * (100 - line.maxDiscountPct)) / 100);
  if (line.priceCents < floor) return { ok: true, approval: 'needed', why: 'below_limit' };
  return { ok: true, approval: null };
}

/** Line total in cents for a decimal quantity ('1.5'), rounded half up. */
export function lineTotal(quantity: string, unitCents: number): number {
  const m = /^(-?)(\d+)(?:\.(\d{1,3}))?$/.exec(quantity.trim());
  if (!m) throw new RangeError(`not a quantity: ${quantity}`);
  const thousandths = BigInt(m[2]! + (m[3] ?? '').padEnd(3, '0')) * (m[1] ? -1n : 1n);
  const raw = thousandths * BigInt(unitCents);
  const q = raw / 1000n;
  const r = raw % 1000n;
  const abs = r < 0n ? -r : r;
  return Number(abs * 2n >= 1000n ? q + (raw < 0n ? -1n : 1n) : q);
}

/**
 * An option's margin (BL-059): the price less our cost, over the price, on the lines that have a cost,
 * a percent to one decimal, rounded down. A line that sells work or goods with no cost on record is
 * left out of both sides and counted as uncosted, so an uncosted line can't lift a thin option over
 * the minimum. Discounts come off the price side, since they are what the customer doesn't pay. Null
 * when no line has a cost, or the costed lines come to nothing after discounts.
 */
export function optionMargin(
  lines: readonly { type: string; quantity: string; totalCents: number; unitCostCents: number | null }[],
): { pct: number | null; uncosted: number; costed: number } {
  let price = 0;
  let cost = 0;
  let uncosted = 0;
  let costed = 0;
  for (const l of lines) {
    if (l.type === 'discount') {
      if (l.totalCents < 0) price += l.totalCents;
    } else if (l.unitCostCents !== null) {
      price += l.totalCents;
      cost += lineTotal(l.quantity, l.unitCostCents);
      costed++;
    } else uncosted++;
  }
  if (!costed || price <= 0) return { pct: null, uncosted, costed };
  // a price far under its cost goes very negative ($0.10 on $100 is −99,900 %): held at the floor
  // the office's record of an OK can keep
  return { pct: Math.max(-99_999.9, Math.floor(((price - cost) * 1000) / price) / 10), uncosted, costed };
}

export type MarginVerdict = 'off' | 'not_checked' | 'ok' | 'warn' | 'below_min';

/** Where an option's margin stands against the company's minimum and warning (0 is off for either). */
export function checkMargin(pct: number | null, minPct: number, warnPct: number): MarginVerdict {
  if (!minPct && !warnPct) return 'off';
  if (pct === null) return 'not_checked';
  if (minPct && pct < minPct) return 'below_min';
  if (warnPct && pct < warnPct) return 'warn';
  return 'ok';
}

export type SellDisposition = 'sold_now' | 'sold_later';

export interface SellCheck {
  estimateStatus: string;
  frozen: boolean;
  jobStatus: string;
  optionBelongs: boolean;
  pendingApprovals: number;
  /** BL-059: the option's margin is under the minimum and the office hasn't OK'd it. */
  marginWaits?: boolean;
  disposition: SellDisposition;
  isScopeChange: boolean;
  invoiceEffect: 'append' | 'replace' | null;
  invoiceStatus: string | null;
}

/** Selling is a decision (sync-protocol §6): refused, in plain words, when it no longer fits. */
export function checkSell(c: SellCheck): { ok: true } | { ok: false; reason: string } {
  if (c.estimateStatus === 'sold' || c.frozen)
    return { ok: false, reason: 'This estimate was already signed. Any change is a new estimate.' };
  if (!c.optionBelongs) return { ok: false, reason: 'That option is not part of this estimate.' };
  if (c.jobStatus === 'cancelled')
    return { ok: false, reason: 'The office cancelled this job, so nothing can be sold on it.' };
  if (c.pendingApprovals > 0)
    return {
      ok: false,
      reason: 'A price on this option needs the office’s OK first. Ask them, or sell it at the book price.',
    };
  if (c.marginWaits)
    return {
      ok: false,
      reason:
        'This option is under the company’s minimum margin: the office needs to OK it before it can be sold.',
    };
  if (c.isScopeChange && !c.invoiceEffect)
    return { ok: false, reason: 'Say whether this change adds to the work or replaces it.' };
  if (c.disposition === 'sold_now') {
    if (c.jobStatus === 'completed')
      return {
        ok: false,
        reason: 'The job is already completed; sell it for later and the office books it.',
      };
    if (c.invoiceStatus && c.invoiceStatus !== 'draft')
      return {
        ok: false,
        reason: 'Today’s invoice is already finalised; sell it for later and the office books it.',
      };
  }
  return { ok: true };
}
